import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { routeConfig, VIEW_MODE_MAP, PERMISSION_VIEW_MAP } from "@/config/routeConfig";
import { useRole } from "./use-role";
import { normalizePermission } from "./use-auth";
import { buildPathFromConfigPath, getFacilityContext, getPortfolioContext } from "@/utils/route-context";

export const useJumpView = () => {
    const navigate = useNavigate();
    const permission = normalizePermission(useSelector((state) => state.Auth.permission));
    const role = useRole();
    const facilityContext = useSelector((state) => getFacilityContext(state));
    const portfolioContext = useSelector((state) => getPortfolioContext(state));

    const hasMenuPermission = useCallback((menuCode) => {
        if (role === 1) {
            return true;
        }

        if (!menuCode) {
            return true;
        }

        return (permission[menuCode] || 0) > 0;
    }, [permission, role]);

    const findFirstAccessiblePath = useCallback((menuItems) => {
        for (const item of menuItems) {
            if (item.path && hasMenuPermission(item.code)) {
                return item.path;
            }

            if (item.children && item.children.length > 0) {
                const childPath = findFirstAccessiblePath(item.children);
                if (childPath) {
                    return childPath;
                }
            }
        }
        return null;
    }, [hasMenuPermission]);

    return useCallback((viewMode) => {
        const viewType = VIEW_MODE_MAP[viewMode];
        if (!viewType) {
            console.error(`Invalid viewMode: ${viewMode}`);
            return;
        }

        const viewPermissionKey = PERMISSION_VIEW_MAP[viewType];
        if (!hasMenuPermission(viewPermissionKey)) {
            console.error(`User doesn't have permission for ${viewType} view`);
            return;
        }

        const viewRoutes = routeConfig[viewType];
        if (!viewRoutes || viewRoutes.length === 0) {
            console.error(`No routes configured for ${viewType} view`);
            return;
        }

        const firstAccessiblePath = findFirstAccessiblePath(viewRoutes);
        const routeContext = viewType === "facility" ? facilityContext : portfolioContext;

        if (firstAccessiblePath) {
            navigate(buildPathFromConfigPath(firstAccessiblePath, viewType, routeContext) || "/switch-view");
        } else {
            navigate(buildPathFromConfigPath(`/${viewType}/404`, viewType, routeContext) || "/switch-view");
        }
    }, [facilityContext, findFirstAccessiblePath, hasMenuPermission, navigate, portfolioContext]);
};
